/**
 * Remembering which sets somebody has already looked at.
 *
 * Without this the scan is honest and useless: it finds the same six near
 * identical selfies every time, and having decided to keep all six you are
 * shown them again on the next visit, for ever. A tool that cannot take "yes,
 * I know, leave them alone" for an answer is one people stop opening.
 *
 * A set is identified by its members, not by its position or the order they
 * came back in, so the same six photographs are the same set tomorrow. Adding a
 * seventh makes it a *different* set and it is offered again — which is right:
 * there is a new photograph in it that nobody has judged.
 */

/**
 * A short, stable name for a set of file ids.
 *
 * FNV-1a over the sorted ids. Not a security hash and not trying to be — the
 * consequence of a collision is one set of duplicates not being offered, and
 * with 32 bits over the few hundred sets a library produces that is not going
 * to happen. What matters is that it is stable across sessions and short enough
 * to keep a few hundred of them in storage.
 */
export function setSignature(fileIds: readonly string[]): string {
  let hash = 0x811c9dc5;
  for (const id of [...fileIds].sort()) {
    for (let index = 0; index < id.length; index++) {
      hash ^= id.charCodeAt(index);
      // The FNV prime, as shifts, because a 32-bit multiply in JavaScript
      // silently goes through a double and loses the low bits.
      hash =
        (hash +
          ((hash << 1) +
            (hash << 4) +
            (hash << 7) +
            (hash << 8) +
            (hash << 24))) >>>
        0;
    }
    // A separator, so ["ab","c"] and ["a","bc"] are not the same set.
    hash ^= 0x2c;
    hash =
      (hash +
        ((hash << 1) +
          (hash << 4) +
          (hash << 7) +
          (hash << 8) +
          (hash << 24))) >>>
      0;
  }
  return hash.toString(16).padStart(8, "0");
}

const KEY = "fc.duplicates.reviewed";

/** Cap the history, so a large library cannot fill storage over time. */
const MAX_REMEMBERED = 2000;

function storageKey(groupId: string): string {
  return `${KEY}.${groupId}`;
}

export function loadReviewed(groupId: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(storageKey(groupId));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v) => typeof v === "string"))
      : new Set();
  } catch {
    // Private browsing, or storage turned off. Nothing is remembered, which is
    // the old behaviour rather than a failure.
    return new Set();
  }
}

export function saveReviewed(
  groupId: string,
  signatures: ReadonlySet<string>,
): void {
  try {
    // Newest kept: an old signature refers to photographs that have probably
    // been dealt with or deleted, and dropping it merely offers a set again.
    const trimmed = [...signatures].slice(-MAX_REMEMBERED);
    window.localStorage.setItem(storageKey(groupId), JSON.stringify(trimmed));
  } catch {
    // Nothing to do. The decision still holds for this session.
  }
}
